/**
 * G-9.2 kit: minimal stroke glyphs for the sidebar navigation (HIG-style
 * grouped nav). One consistent family: 24px viewBox, currentColor stroke,
 * 1.7 width, round caps — reads cleanly at 16px inside the icon tile.
 * Add new names here; never inline ad-hoc SVGs in nav items. Build & send
 * uses the owner's supplied artwork, shared with its home shortcut.
 */

export type NavIconName =
  | "shop"
  | "pin"
  | "inbox"
  | "clock"
  | "calendar"
  | "bag"
  | "flower"
  | "buildSend"
  | "send"
  | "receipt"
  | "banknote"
  | "chart"
  | "sprout"
  | "list"
  | "cart"
  | "storefront"
  | "pencil"
  | "truck"
  | "layers"
  | "tag"
  | "tray"
  | "flag"
  | "sparkles"
  | "box"
  | "map"
  | "people"
  | "person"
  | "sun"
  | "bike"
  | "barcode"
  | "search"
  | "addSquare"
  | "alert"
  | "chevronDown"
  | "close"
  | "more";

const PATHS: Record<Exclude<NavIconName, "buildSend">, React.ReactNode> = {
  shop: (
    <>
      <path d="M4 9.5 5.2 5h13.6L20 9.5" />
      <path d="M4 9.5a2.6 2.6 0 0 0 5.3 0 2.6 2.6 0 0 0 5.4 0 2.6 2.6 0 0 0 5.3 0" />
      <path d="M5.5 12v7.5h13V12" />
      <path d="M9.5 19.5v-4.5h5v4.5" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-6.5-5.4-6.5-10.2A6.5 6.5 0 0 1 12 4.3a6.5 6.5 0 0 1 6.5 6.5C18.5 15.6 12 21 12 21Z" />
      <circle cx="12" cy="10.7" r="2.2" />
    </>
  ),
  inbox: (
    <>
      <path d="M4 13.5V18a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 18v-4.5" />
      <path d="M4 13.5h4.5a3.5 3.5 0 0 0 7 0H20" />
      <path d="M12 4v6.5m0 0 2.6-2.6M12 10.5 9.4 7.9" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="6" width="16" height="14" rx="2" />
      <path d="M8 4v3.5M16 4v3.5M4 10.5h16" />
    </>
  ),
  bag: (
    <>
      <path d="M6 8.5h12l-.9 10.2a1.8 1.8 0 0 1-1.8 1.8H8.7a1.8 1.8 0 0 1-1.8-1.8L6 8.5Z" />
      <path d="M9 10.5v-3a3 3 0 0 1 6 0v3" />
    </>
  ),
  flower: (
    <>
      <circle cx="12" cy="10" r="2.1" />
      <path d="M12 7.9a2.6 2.6 0 1 1 2.4-3.7A2.6 2.6 0 1 1 16.5 8a2.6 2.6 0 1 1-2.1 4.1M12 7.9a2.6 2.6 0 1 0-2.4-3.7A2.6 2.6 0 1 0 7.5 8a2.6 2.6 0 1 0 2.1 4.1" />
      <path d="M12 12.1V21m0-4.5c-1.8 0-3.6-1-4.3-2.6M12 18c1.8-.7 3.3-1.4 4.2-3.1" />
    </>
  ),
  send: (
    <>
      <path d="M20 4 4 10.5l6 2.5 2.5 6L20 4Z" />
      <path d="m10 13 4.5-4.5" />
    </>
  ),
  receipt: (
    <>
      <path d="M6.5 4h11v16l-2.2-1.5L13.1 20l-2.2-1.5L8.7 20l-2.2-1.5V4Z" />
      <path d="M9.5 8.5h5M9.5 12h5" />
    </>
  ),
  banknote: (
    <>
      <rect x="3.5" y="7" width="17" height="10" rx="1.8" />
      <circle cx="12" cy="12" r="2.4" />
      <path d="M6.5 10v.01M17.5 14v.01" />
    </>
  ),
  chart: (
    <>
      <path d="M4.5 19.5h15" />
      <path d="M7 19.5v-6M12 19.5V8.5M17 19.5v-9" />
    </>
  ),
  sprout: (
    <>
      <path d="M12 20.5v-7" />
      <path d="M12 13.5c0-3.6-2.6-6-6.5-6 0 3.8 2.7 6 6.5 6Z" />
      <path d="M12 11.5c0-3 2.2-5 5.5-5 0 3.1-2.3 5-5.5 5Z" />
    </>
  ),
  list: (
    <>
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <path d="m4 6.3 1 1 1.8-1.8M4 11.8l1 1 1.8-1.8M4 17.3l1 1 1.8-1.8" />
    </>
  ),
  cart: (
    <>
      <path d="M4 5h2l2.2 10.5h9.6L20 8H7" />
      <circle cx="9.5" cy="19" r="1.4" />
      <circle cx="16.5" cy="19" r="1.4" />
    </>
  ),
  storefront: (
    <>
      <path d="M5 10v9.5h14V10" />
      <path d="M4 7.5 5.5 4h13L20 7.5a2.7 2.7 0 0 1-5.4.3 2.7 2.7 0 0 1-5.2 0A2.7 2.7 0 0 1 4 7.5Z" />
      <path d="M9 19.5V14h6v5.5" />
    </>
  ),
  pencil: (
    <>
      <path d="M5 19h14" />
      <path d="M6.5 15.5 15.8 6.2a1.9 1.9 0 0 1 2.7 2.7L9.2 18.2 5.5 19l.9-3.5Z" />
    </>
  ),
  truck: (
    <>
      <path d="M3.5 6.5h11V17h-11z" />
      <path d="M14.5 10h3.4l2.6 3v4h-2.6" />
      <circle cx="7.5" cy="17.8" r="1.6" />
      <circle cx="16.5" cy="17.8" r="1.6" />
    </>
  ),
  layers: (
    <>
      <path d="m12 4 8 4.2-8 4.2-8-4.2L12 4Z" />
      <path d="m5 12.2 7 3.7 7-3.7M5 15.9l7 3.7 7-3.7" />
    </>
  ),
  tag: (
    <>
      <path d="M4.5 4.5h7l8 8-7 7-8-8v-7Z" />
      <circle cx="8.8" cy="8.8" r="1.3" />
    </>
  ),
  /* G-72: same flag as the kiosk button, so the place you report a bug and
     the place you read them share one symbol. */
  /* G-149: the three stars AI tools use as their mark — AI control. */
  sparkles: (
    <>
      <path d="M10 4.5l1.7 5.3 5.3 1.7-5.3 1.7L10 18.5l-1.7-5.3L3 11.5l5.3-1.7z" />
      <path d="M18 2.6l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" />
      <path d="M18.4 15.4l.6 1.7 1.7.6-1.7.6-.6 1.7-.6-1.7-1.7-.6 1.7-.6z" />
    </>
  ),
  flag: (
    <>
      <path d="M5.5 21V3.8" />
      <path d="M5.5 4.4h11.9l-2.3 4.1 2.3 4.1H5.5" />
    </>
  ),
  tray: (
    <>
      <path d="M4.5 5.5h15V18a1.5 1.5 0 0 1-1.5 1.5H6A1.5 1.5 0 0 1 4.5 18V5.5Z" />
      <path d="M4.5 13h4a3.5 3.5 0 0 0 7 0h4" />
    </>
  ),
  /**
   * G-83 Inventory. A carton with a seam down the lid, not another tray or tag
   * — `tray` already means catalog staging and `tag` means manage items, and a
   * shared glyph would make two different destinations read as one.
   */
  box: (
    <>
      <path d="M4 8.2 12 4.5l8 3.7v7.6L12 19.5 4 15.8V8.2Z" />
      <path d="M4 8.2 12 12l8-3.8" />
      <path d="M12 12v7.5" />
    </>
  ),
  map: (
    <>
      <path d="m9 5-4.5 1.8v12L9 17l6 2 4.5-1.8v-12L15 7 9 5Z" />
      <path d="M9 5v12m6-10v12" />
    </>
  ),
  people: (
    <>
      <circle cx="9" cy="9" r="3" />
      <path d="M3.5 19.5c.6-3 2.9-4.7 5.5-4.7s4.9 1.7 5.5 4.7" />
      <path d="M15.5 6.7a2.7 2.7 0 1 1 1.3 5.2M17 14.9c2 .5 3.2 1.9 3.6 4" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8.2" r="3.4" />
      <path d="M5 20c.8-4 3.7-6 7-6s6.2 2 7 6" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="3.8" />
      <path d="M12 3.5V5.6M12 18.4v2.1M3.5 12h2.1m12.8 0h2.1M6 6l1.5 1.5M18 6l-1.5 1.5M6 18l1.5-1.5M18 18l-1.5-1.5" />
    </>
  ),
  bike: (
    <>
      <circle cx="6" cy="16.5" r="3.2" />
      <circle cx="18" cy="16.5" r="3.2" />
      <path d="M6 16.5 9.5 9h5M18 16.5 15 9h-1.8M9.5 9l3.5 7.5" />
      <path d="M13.2 6.5h2.6" />
    </>
  ),
  /* G-132 inventory page: scan, search, new item, low-stock, menus. */
  barcode: (
    <>
      <path d="M4 6v12M7.5 6v12M11 6v12M14.5 6v12M17.5 6v12M20 6v12" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.4-4.4" />
    </>
  ),
  /* "New item": a rounded square left open at its lower right, where a plus sits. */
  addSquare: (
    <>
      <path d="M12 20H9a5 5 0 0 1-5-5V9a5 5 0 0 1 5-5h6a5 5 0 0 1 5 5v3" />
      <path d="M18 15v6M15 18h6" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4 3.5 19h17L12 4Z" />
      <path d="M12 10v4.2M12 16.8v.2" />
    </>
  ),
  chevronDown: <path d="m6 9.5 6 6 6-6" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.2" />
      <circle cx="12" cy="12" r="1.2" />
      <circle cx="18.5" cy="12" r="1.2" />
    </>
  ),
};

export function NavIcon({ name, size = 16 }: { name: NavIconName; size?: number }) {
  if (name === "buildSend") {
    return <span className="build-send-icon" aria-hidden="true" />;
  }

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
